import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileUpload } from '@/components/ui/file-upload';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, FileText, ExternalLink, Trash2, Check, AlertTriangle } from 'lucide-react';
import { format, parse } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { captureError } from '@/lib/logger';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { formatMoney } from '@/lib/format';
import { shouldShowApproxChip, approxContainerCount, type ContainerMember } from './ProcurementContainers';
import { diffParsedVinsAgainstMembership } from './customsAllocation';
import type { ContainerBoe } from './ContainerCustomsSheet';

// ─── Types ──────────────────────────────────────────────────────────────────

type MultiDocType = 'tax_bill' | 'levy' | 'other';

interface ParsedBoeItem {
    vin: string;
    item_no: string | null;
    /** Null when the parser couldn't find a tax figure for this VIN — blocks Confirm (a NOT NULL
     * DB column), surfaced as one of `warnings` below rather than silently defaulted. */
    tax_amount: number | null;
    description: string | null;
    matched_vehicle_id: number | null;
    matched_vehicle_label: string | null;
    current_bl_tracking_no: string | null;
}

interface ParsedBoeResponse {
    /** boe_no/bl_no/boe_date/total are nullable — the parser is regex-based and may fail to find
     * one on a malformed/unexpected document layout. Each failure is also pushed into `warnings`;
     * Confirm is blocked (not just warned) when any required field is missing since the DB columns
     * are NOT NULL. */
    boe_no: string | null;
    bl_no: string | null;
    boe_date: string | null;
    office_code: string | null;
    reference_no: string | null;
    total: number | null;
    line_items_total: number | null;
    currency: string;
    vehicle_count: number;
    items: ParsedBoeItem[];
    warnings: string[];
}

interface CustomsDocumentsPanelProps {
    fullMembers: ContainerMember[];
    fullSize: number;
    confirmedBl: string | null;
    boe: ContainerBoe | null;
    isLoading: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DOC_TYPE_META: Record<MultiDocType, { label: string; folder: string }> = {
    tax_bill: { label: 'Tax Bill', folder: 'tax-bill' },
    // The TMA1-L document's title — its single fee line happens to be the GIFF levy, but the
    // document itself is the CHA Association membership levy GRA collects on their behalf.
    levy: { label: 'Customs House Agent Association Membership Levy', folder: 'levy' },
    other: { label: 'Other', folder: 'other' },
};

function formatMoneyOrDash(amount: number | null, currency: string): string {
    return amount == null ? '—' : formatMoney(amount, currency);
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function CustomsDocumentsPanel({ fullMembers, fullSize, confirmedBl, boe, isLoading }: CustomsDocumentsPanelProps) {
    const queryClient = useQueryClient();

    // Bill of Entry slot state
    const [boeFile, setBoeFile] = useState<File | null>(null);
    const [parsing, setParsing] = useState(false);
    const [parsedBoe, setParsedBoe] = useState<ParsedBoeResponse | null>(null);

    // Multi-doc slots state
    const [pendingFiles, setPendingFiles] = useState<Record<MultiDocType, File[]>>({ tax_bill: [], levy: [], other: [] });
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; label: string } | null>(null);

    // Memoized — diffing every parsed VIN against every member VIN is O(n*m) and was otherwise
    // recomputed on every render (including unrelated state changes like pendingFiles edits).
    const diff = useMemo(
        () => parsedBoe ? diffParsedVinsAgainstMembership(parsedBoe.items.map(i => i.vin), fullMembers.map(m => m.vin)) : null,
        [parsedBoe, fullMembers],
    );

    const invalidateAfterBoeChange = () => {
        queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
        queryClient.invalidateQueries({ queryKey: ['container-boe'] });
    };

    const handleParseBoe = async () => {
        if (!boeFile) return;
        setParsing(true);
        try {
            // Lazy-loaded — pdfjs-dist is heavy and only needed when an admin actually parses a BOE
            // (mirrors SbtImportSheet.tsx's convention for the same helper).
            const { extractPdfText } = await import('@/lib/sbt-pdf');
            const text = await extractPdfText(boeFile);
            const { data, error } = await supabase.functions.invoke('parse-bill-of-entry', { body: { text } });
            if (error) throw error;
            setParsedBoe(data as ParsedBoeResponse);
        } catch (e: any) {
            captureError(e);
            toast.error('Failed to parse Bill of Entry', { description: e.message });
        } finally {
            setParsing(false);
        }
    };

    const confirmBoeMutation = useMutation({
        mutationFn: async () => {
            if (!parsedBoe || !boeFile) throw new Error('Nothing to confirm');
            // Defense in depth — the Confirm button is already disabled while true, but the DB
            // columns below are NOT NULL and would otherwise fail with a raw Postgres error.
            if (!parsedBoe.boe_no || !parsedBoe.bl_no || !parsedBoe.boe_date || parsedBoe.total == null || parsedBoe.items.some(i => i.tax_amount == null)) {
                throw new Error('Required field(s) could not be parsed from this document — cannot confirm');
            }
            const boeNo = parsedBoe.boe_no;
            const blNo = parsedBoe.bl_no;
            const boeDate = parsedBoe.boe_date;
            const total = parsedBoe.total;

            // 0. Dedupe parsed items by VIN (keep first occurrence) — defense in depth alongside the
            // parser's own dedupe: a duplicated VIN reaching this point would otherwise violate
            // bill_of_entry_items' (boe_id, vin) constraint and double-count toward the container's
            // tax total.
            const seenVins = new Set<string>();
            const dedupedItems: ParsedBoeItem[] = [];
            let duplicateVinCount = 0;
            for (const item of parsedBoe.items) {
                const normalizedVin = item.vin.trim().toUpperCase();
                if (seenVins.has(normalizedVin)) {
                    duplicateVinCount++;
                    continue;
                }
                seenVins.add(normalizedVin);
                dedupedItems.push(item);
            }
            if (duplicateVinCount > 0) {
                toast.warning(`${duplicateVinCount} duplicate VIN line${duplicateVinCount > 1 ? 's' : ''} on the document were dropped (first occurrence kept).`);
            }

            // 1. Upload the source PDF.
            const path = `bill-of-entry/${crypto.randomUUID()}.pdf`;
            const { error: upErr } = await supabase.storage.from('procurement-documents').upload(path, boeFile);
            if (upErr) throw upErr;
            const { data: urlData } = supabase.storage.from('procurement-documents').getPublicUrl(path);

            // 2. Insert-or-reuse the container-level Bill of Entry row. Upsert on boe_no (its UNIQUE
            // constraint) rather than a blind insert, so retrying after a failure in step 3/4 below
            // reuses the same row instead of erroring on the constraint.
            const { data: boeRow, error: boeErr } = await supabase
                .from('bills_of_entry')
                .upsert({
                    boe_no: boeNo,
                    bl_no: blNo,
                    boe_date: boeDate,
                    office_code: parsedBoe.office_code,
                    reference_no: parsedBoe.reference_no,
                    total_amount: total,
                    line_items_total: parsedBoe.line_items_total,
                    currency: (parsedBoe.currency as 'GHS' | 'USD') || 'GHS',
                    vehicle_count: parsedBoe.vehicle_count ?? dedupedItems.length,
                }, { onConflict: 'boe_no' })
                .select('id')
                .single();
            if (boeErr) throw boeErr;
            const boeId = (boeRow as any).id as number;

            // 3. Upsert one item per parsed VIN (nullable vehicle_id for unmatched VINs) — onConflict
            // (boe_id, vin) so a retry reuses/updates existing item rows instead of duplicating them.
            const itemsPayload = dedupedItems.map(item => ({
                boe_id: boeId,
                vehicle_id: item.matched_vehicle_id ?? null,
                vin: item.vin,
                item_no: item.item_no,
                description: item.description,
                tax_amount: item.tax_amount as number,
            }));
            const { error: itemsErr } = await supabase.from('bill_of_entry_items').upsert(itemsPayload, { onConflict: 'boe_id,vin' });
            if (itemsErr) throw itemsErr;

            // 4. Record the uploaded document — tolerant of retry. A partial unique index allows only
            // one 'bill_of_entry' doc per boe_id, so check for an existing row first rather than
            // blindly inserting (which would violate that index if step 4 already succeeded once).
            const { data: existingBoeDocRow } = await supabase
                .from('procurement_documents')
                .select('id')
                .eq('boe_id', boeId)
                .eq('doc_type', 'bill_of_entry')
                .maybeSingle();
            if (!existingBoeDocRow) {
                const { error: docErr } = await supabase.from('procurement_documents').insert({
                    boe_id: boeId,
                    doc_type: 'bill_of_entry',
                    file_url: urlData.publicUrl,
                    original_filename: boeFile.name,
                });
                if (docErr) throw docErr;
            }

            // 5. BL backfill — ONLY where bl_tracking_no IS NULL; never clobber a different existing BL
            //    (surfaced as a warning in the preview instead, see `blConflicts` below).
            const matchedVehicleIds = dedupedItems
                .map(i => i.matched_vehicle_id)
                .filter((id): id is number => id != null);
            if (matchedVehicleIds.length > 0) {
                const { error: blErr } = await supabase
                    .from('vehicle_procurement')
                    .update({ bl_tracking_no: blNo })
                    .in('vehicle_id', matchedVehicleIds)
                    .is('bl_tracking_no', null);
                if (blErr) throw blErr;
            }
        },
        onSuccess: () => {
            toast.success('Bill of Entry recorded');
            setBoeFile(null);
            setParsedBoe(null);
            invalidateAfterBoeChange();
        },
        onError: (e: Error) => {
            captureError(e);
            toast.error(e.message || 'Failed to record Bill of Entry');
        },
    });

    const uploadDocsMutation = useMutation({
        mutationFn: async ({ docType, files }: { docType: MultiDocType; files: File[] }) => {
            if (!boe) throw new Error('Upload the Bill of Entry first');
            const folder = DOC_TYPE_META[docType].folder;
            for (const file of files) {
                const ext = file.name.split('.').pop() || 'pdf';
                const path = `${folder}/${crypto.randomUUID()}.${ext}`;
                const { error: upErr } = await supabase.storage.from('procurement-documents').upload(path, file);
                if (upErr) throw upErr;
                const { data: urlData } = supabase.storage.from('procurement-documents').getPublicUrl(path);
                const { error: insErr } = await supabase.from('procurement_documents').insert({
                    boe_id: boe.id,
                    doc_type: docType,
                    file_url: urlData.publicUrl,
                    original_filename: file.name,
                });
                if (insErr) throw insErr;
            }
        },
        onSuccess: (_result, vars) => {
            toast.success(`${DOC_TYPE_META[vars.docType].label} uploaded`);
            setPendingFiles(prev => ({ ...prev, [vars.docType]: [] }));
            queryClient.invalidateQueries({ queryKey: ['container-boe'] });
        },
        onError: (e: Error) => {
            captureError(e);
            toast.error(e.message || 'Failed to upload document');
        },
    });

    const deleteDocMutation = useMutation({
        mutationFn: async (docId: number) => {
            const { error } = await supabase.from('procurement_documents').delete().eq('id', docId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Document deleted');
            queryClient.invalidateQueries({ queryKey: ['container-boe'] });
            setDeleteTarget(null);
        },
        onError: (e: Error) => {
            captureError(e);
            toast.error(e.message || 'Failed to delete document');
        },
    });

    if (isLoading) {
        return (
            <div className="py-12 text-center">
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
            </div>
        );
    }

    const isFullMatch = !!diff && diff.parsedOnlyVins.length === 0 && diff.unmatchedMemberVins.length === 0;
    const isOversized = shouldShowApproxChip(fullSize, confirmedBl);
    const totalsDiffer = !!parsedBoe && parsedBoe.line_items_total != null && parsedBoe.total != null
        && Math.abs(parsedBoe.line_items_total - parsedBoe.total) > 0.01;
    const blConflicts = parsedBoe
        ? parsedBoe.items.filter(i => i.current_bl_tracking_no && i.current_bl_tracking_no !== parsedBoe.bl_no)
        : [];
    const existingBoeDoc = boe?.procurement_documents.find(d => d.doc_type === 'bill_of_entry') ?? null;
    // Missing required fields (regex parse failure) BLOCK confirming — unlike totals/membership
    // mismatches, there's no value to fall back to (the DB columns are NOT NULL). Each is also
    // already surfaced via `parsedBoe.warnings`.
    const missingRequiredFields = !!parsedBoe && (
        !parsedBoe.boe_no || !parsedBoe.bl_no || !parsedBoe.boe_date || parsedBoe.total == null
        || parsedBoe.items.some(i => i.tax_amount == null)
    );

    return (
        <div className="space-y-6 pb-6">
            {/* Bill of Entry slot (single) */}
            <section className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        Bill of Entry
                    </h4>
                    {boe && (
                        <Badge variant="secondary" className="gap-1">
                            <Check className="h-3 w-3" /> Recorded
                        </Badge>
                    )}
                </div>

                {boe ? (
                    <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                            <div><span className="text-muted-foreground">BOE No.</span><p className="font-medium">{boe.boe_no}</p></div>
                            <div><span className="text-muted-foreground">BL No.</span><p className="font-medium">{boe.bl_no}</p></div>
                            <div><span className="text-muted-foreground">Date</span><p className="font-medium">{format(parse(boe.boe_date.substring(0, 10), 'yyyy-MM-dd', new Date()), 'MMM d, yyyy')}</p></div>
                            <div><span className="text-muted-foreground">Total</span><p className="font-medium">{formatMoney(boe.total_amount, boe.currency)}</p></div>
                        </div>
                        {boe.line_items_total != null && Math.abs(boe.line_items_total - boe.total_amount) > 0.01 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                Line items sum to {formatMoney(boe.line_items_total, boe.currency)} — differs from the printed total by {formatMoney(Math.abs(boe.line_items_total - boe.total_amount), boe.currency)}.
                            </p>
                        )}
                        {existingBoeDoc && (
                            <Button variant="outline" size="sm" onClick={() => openSignedUrl(existingBoeDoc.file_url)}>
                                <FileText className="h-4 w-4 mr-2" /> View document <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                        )}
                    </div>
                ) : parsedBoe ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-muted-foreground">BOE No.</span><p className="font-medium">{parsedBoe.boe_no ?? <span className="text-red-600">Not found</span>}</p></div>
                            <div><span className="text-muted-foreground">BL No.</span><p className="font-medium">{parsedBoe.bl_no ?? <span className="text-red-600">Not found</span>}</p></div>
                            <div><span className="text-muted-foreground">Date</span><p className="font-medium">{parsedBoe.boe_date ?? <span className="text-red-600">Not found</span>}</p></div>
                            <div><span className="text-muted-foreground">Total</span><p className="font-medium">{formatMoneyOrDash(parsedBoe.total, parsedBoe.currency)}</p></div>
                        </div>

                        {totalsDiffer && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                Line items sum to {formatMoney(parsedBoe.line_items_total as number, parsedBoe.currency)} — differs from the printed total by{' '}
                                {formatMoney(Math.abs((parsedBoe.line_items_total as number) - parsedBoe.total), parsedBoe.currency)}. Both are recorded — this never blocks confirming.
                            </p>
                        )}

                        {isFullMatch ? (
                            <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 shrink-0" /> All {fullMembers.length} member vehicles matched
                            </p>
                        ) : (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                {diff && diff.unmatchedMemberVins.length > 0 && `${diff.unmatchedMemberVins.length} member vehicle(s) not on this document. `}
                                {diff && diff.parsedOnlyVins.length > 0 && `${diff.parsedOnlyVins.length} VIN(s) on the document aren't in this container.`}
                            </p>
                        )}

                        {isOversized && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                This cluster is an estimate (≈{approxContainerCount(fullSize)} containers) — double-check the parsed BL matches the physical container.
                            </p>
                        )}

                        {blConflicts.length > 0 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                {blConflicts.length} vehicle(s) already have a different BL recorded — it will NOT be overwritten.
                            </p>
                        )}

                        {parsedBoe.warnings.length > 0 && (
                            <ul className="text-xs text-amber-600 list-disc pl-4 space-y-0.5">
                                {parsedBoe.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        )}

                        {missingRequiredFields && (
                            <p className="text-xs text-red-600 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                Required field(s) missing (see warnings above) — this document cannot be confirmed until it's fixed or re-uploaded.
                            </p>
                        )}

                        <div className="rounded-md border overflow-x-auto max-h-56 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50 sticky top-0">
                                    <tr>
                                        <th className="text-left px-2 py-1.5 font-medium">VIN</th>
                                        <th className="text-left px-2 py-1.5 font-medium">Vehicle</th>
                                        <th className="text-right px-2 py-1.5 font-medium">Tax</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parsedBoe.items.map((item, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="px-2 py-1.5 font-mono">{item.vin}</td>
                                            <td className="px-2 py-1.5">
                                                {item.matched_vehicle_label ?? <span className="text-amber-600">Unmatched</span>}
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                                {item.tax_amount == null
                                                    ? <span className="text-red-600">Not found</span>
                                                    : formatMoney(item.tax_amount, parsedBoe.currency)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setParsedBoe(null)}>
                                Discard
                            </Button>
                            <Button size="sm" onClick={() => confirmBoeMutation.mutate()} disabled={missingRequiredFields || confirmBoeMutation.isPending}>
                                {confirmBoeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Confirm
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <FileUpload file={boeFile} setFile={setBoeFile} accept=".pdf" />
                        <Button size="sm" disabled={!boeFile || parsing} onClick={handleParseBoe}>
                            {parsing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Parse Bill of Entry
                        </Button>
                    </>
                )}
            </section>

            {/* Tax Bill / Levy / Other slots (multi-file, enabled once a BOE exists) */}
            {(['tax_bill', 'levy', 'other'] as const).map(type => {
                const meta = DOC_TYPE_META[type];
                const docs = boe?.procurement_documents.filter(d => d.doc_type === type) ?? [];
                const disabled = !boe;
                return (
                    <section key={type} className={cn('rounded-lg border p-4 space-y-3', disabled && 'opacity-60')}>
                        <div className="flex items-center justify-between">
                            <h4 className="font-medium flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                {meta.label}
                            </h4>
                            {docs.length > 0 && <Badge variant="secondary">{docs.length}</Badge>}
                        </div>

                        {disabled ? (
                            <p className="text-xs text-muted-foreground">Upload the Bill of Entry first</p>
                        ) : (
                            <>
                                {docs.length > 0 && (
                                    <ul className="space-y-1.5">
                                        {docs.map(d => (
                                            <li key={d.id} className="flex items-center justify-between gap-2 text-sm rounded-md border px-2.5 py-1.5">
                                                <span className="truncate">{d.original_filename ?? 'document.pdf'}</span>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSignedUrl(d.file_url)} title="View">
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-destructive"
                                                        onClick={() => setDeleteTarget({ id: d.id, label: d.original_filename ?? 'document' })}
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <FileUpload
                                    multiple
                                    files={pendingFiles[type]}
                                    setFiles={(files) => setPendingFiles(prev => ({ ...prev, [type]: files }))}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                {pendingFiles[type].length > 0 && (
                                    <Button
                                        size="sm"
                                        disabled={uploadDocsMutation.isPending}
                                        onClick={() => uploadDocsMutation.mutate({ docType: type, files: pendingFiles[type] })}
                                    >
                                        {uploadDocsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        Upload
                                    </Button>
                                )}
                            </>
                        )}
                    </section>
                );
            })}

            {/* Delete document confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete document?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove <strong>{deleteTarget?.label}</strong> from this container. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteTarget && deleteDocMutation.mutate(deleteTarget.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
